/**
 * SSE client wrapper sobre @microsoft/fetch-event-source (Pattern 2 RESEARCH).
 * Pitfall #4 cerrado: backend wrappea progress events bajo `event: progress`
 * con `data.type` interno; eventos terminales tienen su propio event name.
 *
 * T-04-01-01 (Information Disclosure): NUNCA logear opts.device_token ni
 * incluirlo en stack traces. Errores reportados al usuario sanitizados.
 */
import { cerrarSesion } from '../utils/sesion'
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source'
import type { SseProgressEvent, SseTerminalEvent } from '../types/sse'
import type { ProfileType } from '../types/profile'
import { leerSucursal } from '../hooks/useSucursal'

class FatalError extends Error {}
class RetriableError extends Error {}

export interface StreamChatOptions {
  message: string
  device_id: string
  device_token: string
  session_id?: string
  profile?: ProfileType | null
  signal: AbortSignal
  onProgress: (evt: SseProgressEvent) => void
  onTerminal: (evt: SseTerminalEvent) => void
}

export async function streamChat(opts: StreamChatOptions): Promise<void> {
  await fetchEventSource('/api/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Device-Token': opts.device_token,
    },
    body: JSON.stringify({
      message: opts.message,
      device_id: opts.device_id,
      session_id: opts.session_id,
      profile: opts.profile,
      id_und_neg: leerSucursal()?.id_und_neg,
      modo: 'interactivo', // 12-oct: único modo; el tablero de uso sigue contando por modo
    }),
    signal: opts.signal,
    openWhenHidden: true,  // tablet UX — no abort si ventana se oculta

    async onopen(response) {
      if (
        response.ok &&
        response.headers.get('content-type')?.includes(EventStreamContentType)
      ) {
        return
      }
      // T-04-01-07: NO incluir body de la response en el throw — sanitizado.
      if (response.status === 401) {
        cerrarSesion()  // token vencido, contraseña cambiada o usuario dado de baja
        throw new FatalError('auth_401')
      }
      if (response.status === 403) {
        throw new FatalError(`auth_${response.status}`)
      }
      if (response.status >= 400 && response.status < 500) {
        throw new FatalError(`client_${response.status}`)
      }
      throw new RetriableError()
    },

    onmessage(msg) {
      const data = JSON.parse(msg.data)
      if (msg.event === 'progress') {
        opts.onProgress(data as SseProgressEvent)
      } else if (
        msg.event === 'complete' ||
        msg.event === 'error' ||
        msg.event === 'cancelled' ||
        msg.event === 'timeout'
      ) {
        opts.onTerminal(data as SseTerminalEvent)
      }
    },

    onerror(err) {
      if (err instanceof FatalError) throw err
      // T-04-01-07: error genérico (no incluir body remoto en mensaje)
      throw new FatalError('network_error')
    },

    onclose() {
      // Server cerró sin terminal — no-op; el consumer maneja con el state machine.
    },
  })
}
